import React from 'react';
import { Formik } from 'formik';
import { Button } from '@unmind/design-system';
import { useTranslation } from 'react-i18next';
import {
  CalendarEvent,
  google,
  office365,
  yahoo,
  outlook,
  ics,
} from 'calendar-link';
import { tracking } from 'App/Tracking';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import {
  Title,
  ContentContainer,
  Container,
  ButtonContainer,
  CloseIconButton,
  CloseIcon,
  HeaderContainer,
  Subtitle,
} from './styles';
import { EditableTextField } from './EditableTextField';
import { DateTimeDurationSelectorGroup } from './DateTimeDurationSelectorGroup';
import { Cadence, CadenceDropdown } from './CadenceDropdown';
import { getContentTypeFromLink } from './getContentTypeFromLink';
import { CalendarDropdown, CalendarType } from './CalendarDropdown';

interface AddToCalendarModalProps {
  onClose(): void;
  title: string;
  description?: string;
  contentPath: string;
}

export interface AddToCalendarValues {
  title: string;
  description: string;
  date: Date;
  duration: number;
  cadence: Cadence;
  calendarType?: string;
}

interface OpenCalendar extends AddToCalendarValues {
  link: string;
}

export const AddToCalendarModal = ({
  onClose,
  title,
  description,
  contentPath,
}: AddToCalendarModalProps) => {
  const { t: translate } = useTranslation('shared');

  const initialDate = new Date();
  const roundedMinutes = Math.round(initialDate.getMinutes() / 15) * 15;
  initialDate.setMinutes(roundedMinutes, 0, 0);
  const link = `https://${getSubdomainFromUrl()}.${window.location.hostname
    .split('.')
    .slice(1)
    .join('.')}${contentPath}`;

  const initialValues: AddToCalendarValues = {
    title,
    description: description || '',
    date: initialDate,
    cadence: Cadence.Weekly,
    duration: 10,
  };

  const openCalendar = (values: OpenCalendar) => {
    let calendarLink = '';
    const event: CalendarEvent = {
      title: values.title,
      description: translate('add_to_calendar.modal_description', {
        description: values.description,
        link: values.link,
      }),
      start: values.date,
      duration: [values.duration, 'minutes'],
    };

    const eventWithCadence =
      values.cadence === Cadence.Once
        ? event
        : {
            ...event,
            rRule: `FREQ=${values.cadence}`,
            recur: 'RRULE',
          };

    switch (values.calendarType) {
      case CalendarType.Google:
        calendarLink = google(eventWithCadence);
        break;
      case CalendarType.Office365:
        calendarLink = office365(eventWithCadence);
        break;
      case CalendarType.Yahoo:
        calendarLink = yahoo(eventWithCadence);
        break;
      case CalendarType.Outlook:
        calendarLink = outlook(eventWithCadence);
        break;
      case CalendarType.iCalendar:
        calendarLink = ics(eventWithCadence);
        break;
      default:
        console.error('Unknown calendar type:', values.calendarType);
    }

    if (calendarLink) {
      window.open(calendarLink, '_blank');
    } else {
      console.error(
        'Calendar link generation failed for:',
        values.calendarType,
      );
    }
  };

  const onSubmit = (values: AddToCalendarValues) => {
    tracking.track('add-to-calendar-event-added', {
      calendarType: values.calendarType,
      content: link,
      calendarEventDate: values.date.toISOString(),
      cadence: values.cadence,
      contentType: getContentTypeFromLink(contentPath),
    });
    openCalendar({
      ...values,
      link: `${link}?utm_source=${values.calendarType?.toLowerCase()}_calendar`,
    });
    onClose();
  };

  const cadenceIsSupported: CalendarType[] = [
    CalendarType.Google,
    CalendarType.iCalendar,
  ];

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues}>
      {({ values, handleSubmit, setFieldValue }) => (
        <Container onSubmit={handleSubmit}>
          <HeaderContainer>
            <Title>{translate('add_to_calendar.modal.title')}</Title>
            <CloseIconButton
              onClick={onClose}
              role="button"
              tabIndex={0}
              aria-describedby={translate('add_to_calendar.modal.close')}
            >
              <CloseIcon />
            </CloseIconButton>
          </HeaderContainer>
          <ContentContainer>
            <Subtitle>{translate('add_to_calendar.modal.event_name')}</Subtitle>
            <EditableTextField
              backgroundColour={true}
              accessibleLabel={translate(
                'add_to_calendar.title.accessibility_label',
              )}
              value={values.title}
              onChange={setFieldValue}
              field={'title'}
            />
            <Subtitle>{translate('add_to_calendar.modal.date')}</Subtitle>
            <DateTimeDurationSelectorGroup
              date={values.date}
              duration={values.duration}
              onSelect={setFieldValue}
            />
            {(cadenceIsSupported.includes(
              values.calendarType as CalendarType,
            ) ||
              !values.calendarType) && (
              <CadenceDropdown
                onChange={setFieldValue}
                value={values.cadence}
              />
            )}
            <CalendarDropdown
              onChange={setFieldValue}
              value={values.calendarType}
            />
          </ContentContainer>
          <ButtonContainer>
            <Button
              label={translate('add_to_calendar.modal.close')}
              onClick={onClose}
              type="button"
              size="medium"
            />
            <Button
              variant="primary"
              role="link"
              disabled={values.calendarType ? false : true}
              aria-disabled={values.calendarType ? false : true}
              label={translate('add_to_calendar.modal.button')}
              type="submit"
              size="medium"
            />
          </ButtonContainer>
        </Container>
      )}
    </Formik>
  );
};
